import React, { useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — react-icons + React 19 typings
import { FaPlus as FaPlusRaw } from 'react-icons/fa';
import { teacherService, type TeacherWorkItem } from '../../services/classroom/teacherService';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaPlus: any = FaPlusRaw;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  width: 100%;
  min-width: 0;
`;

const TopRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

const TitleBlock = styled.div`
  min-width: 0;
`;

const PageTitle = styled.h2`
  margin: 0 0 0.35rem;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.35rem, 2.2vw, 1.65rem);
  font-weight: 700;
  color: var(--text);
`;

const PageSubtitle = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  line-height: 1.45;
  max-width: 38rem;
`;

const PrimaryBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Open Sans', sans-serif;
  border: none;
  border-radius: 10px;
  background: var(--primary);
  color: var(--onPrimary);
  cursor: pointer;
  align-self: center;
  text-decoration: none;
  &:hover {
    background: var(--primaryDark);
  }
`;

const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 1rem 1.15rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
`;

const CardTitle = styled.h3`
  margin: 0 0 0.85rem;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.55rem;
`;

const Row = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
  padding: 0.85rem 0.95rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surface);
`;

const RowMain = styled.div`
  min-width: 0;
`;

const RowTitle = styled.div`
  font-weight: 700;
  color: var(--text);
`;

const RowMeta = styled.div`
  margin-top: 0.2rem;
  font-size: 0.82rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const RowActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const GhostBtn = styled.button`
  padding: 0.4rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: 'Open Sans', sans-serif;
  border-radius: 8px;
  border: 1px solid var(--outline);
  background: var(--surfaceVariant);
  color: var(--text);
  cursor: pointer;
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const LiveBtn = styled(GhostBtn)`
  border-color: var(--success, #1a8f5c);
  color: var(--success, #1a8f5c);
  &:hover:not(:disabled) {
    background: color-mix(in srgb, var(--success, #1a8f5c) 10%, var(--surface));
  }
`;

const Empty = styled.p`
  margin: 0;
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-size: 0.9rem;
`;

const ClassroomTeacherQuizzesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const workQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'work', 'quiz'],
    queryFn: () => teacherService.getWork('quiz'),
  });

  const overviewQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'overview'],
    queryFn: teacherService.getOverview,
  });

  const saveMutation = useMutation({
    mutationFn: teacherService.saveWork,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'work', 'quiz'] });
    },
  });

  const publishMutation = useMutation({
    mutationFn: (workId: string) => teacherService.publishWork('quiz', workId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'work', 'quiz'] });
    },
  });

  const classNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of overviewQuery.data ?? []) {
      map.set(row.id, row.name);
    }
    return map;
  }, [overviewQuery.data]);

  const activeRows = useMemo(
    () => (workQuery.data ?? []).filter((row) => !row.archived),
    [workQuery.data]
  );
  const archivedRows = useMemo(
    () => (workQuery.data ?? []).filter((row) => row.archived),
    [workQuery.data]
  );

  const classFilter = searchParams.get('classId')?.trim() ?? '';

  const filteredActive = useMemo(() => {
    if (!classFilter) return activeRows;
    return activeRows.filter((row) => row.class_id === classFilter);
  }, [activeRows, classFilter]);

  const filteredArchived = useMemo(() => {
    if (!classFilter) return archivedRows;
    return archivedRows.filter((row) => row.class_id === classFilter);
  }, [archivedRows, classFilter]);

  const duplicate = (row: TeacherWorkItem) => {
    saveMutation.mutate({
      class_id: row.class_id,
      title: `${row.title} (Copy)`,
      description: row.description ?? null,
      due_at: row.due_at,
      points: row.points,
      archived: false,
      type: 'quiz',
    });
  };

  useEffect(() => {
    const quizIdFromQuery = searchParams.get('quizId')?.trim();
    if (!quizIdFromQuery || !workQuery.data) return;
    if (!workQuery.data.some((q) => q.id === quizIdFromQuery)) return;
    navigate(`/classroom-teacher/quizzes/${quizIdFromQuery}/edit`, { replace: true });
  }, [navigate, searchParams, workQuery.data]);

  if (workQuery.isLoading || overviewQuery.isLoading) {
    return <p style={{ color: 'var(--textSecondary, var(--onSurfaceVariant))' }}>Loading quizzes…</p>;
  }
  if (workQuery.isError || overviewQuery.isError) {
    return <p style={{ color: 'var(--textSecondary, var(--onSurfaceVariant))' }}>Unable to load quizzes.</p>;
  }

  return (
    <Page>
      <TopRow>
        <TitleBlock>
          <PageTitle>Quizzes</PageTitle>
          <PageSubtitle>
            Build quizzes with the same step-by-step editor as assignments. Students see quizzes once you go live.
          </PageSubtitle>
        </TitleBlock>
        <PrimaryBtn to="/classroom-teacher/quizzes/new">
          <FaPlus size={14} aria-hidden />
          New quiz
        </PrimaryBtn>
      </TopRow>

      <Card>
        <CardTitle>ACTIVE QUIZZES</CardTitle>
        {filteredActive.length === 0 ? (
          <Empty>
            {classFilter ? 'No quizzes for this class yet.' : 'No active quizzes yet. Create one to get started.'}
          </Empty>
        ) : (
          <List>
            {filteredActive.map((row) => {
              const isLive = Boolean(row.published_at);
              return (
              <Row key={row.id}>
                <RowMain>
                  <RowTitle>{row.title}</RowTitle>
                  <RowMeta>
                    {classNameById.get(row.class_id) ?? 'Class'}{' '}
                    · {isLive ? 'Live' : 'Not live'}{' '}
                    · Due {row.due_at ? new Date(row.due_at).toLocaleString() : 'TBD'} · {row.points} pts
                  </RowMeta>
                </RowMain>
                <RowActions>
                  {!isLive ? (
                    <LiveBtn
                      type="button"
                      onClick={() => publishMutation.mutate(row.id)}
                      disabled={publishMutation.isPending}
                    >
                      Go live
                    </LiveBtn>
                  ) : null}
                  <GhostBtn type="button" onClick={() => navigate(`/classroom-teacher/quizzes/${row.id}/edit`)}>
                    Edit
                  </GhostBtn>
                  <GhostBtn type="button" onClick={() => duplicate(row)} disabled={saveMutation.isPending}>
                    Duplicate
                  </GhostBtn>
                  <GhostBtn
                    type="button"
                    onClick={() => saveMutation.mutate({ ...row, archived: true })}
                    disabled={saveMutation.isPending}
                  >
                    Archive
                  </GhostBtn>
                </RowActions>
              </Row>
            );
            })}
          </List>
        )}
      </Card>

      <Card>
        <CardTitle>ARCHIVED</CardTitle>
        {filteredArchived.length === 0 ? (
          <Empty>No archived quizzes.</Empty>
        ) : (
          <List>
            {filteredArchived.map((row) => (
              <Row key={row.id}>
                <RowMain>
                  <RowTitle>{row.title}</RowTitle>
                  <RowMeta>{classNameById.get(row.class_id) ?? 'Class'}</RowMeta>
                </RowMain>
              </Row>
            ))}
          </List>
        )}
      </Card>
    </Page>
  );
};

export default ClassroomTeacherQuizzesPage;
